// WFH Requests — WP plugin parity (Screens E6 + E7).
// - "How WFH works" info card (purple)
// - 6-col table: From / To / Reason / Status / Admin Note / Action
// - + Request WFH btn opens RequestWfhModal (3 fields + IP policy callout)

import React, { useEffect, useState } from "react";
import { useToast } from "@chakra-ui/react";
import { api } from "../../lib/api";
import { useShellHeader } from "../../Components/AppShell";
import { Card, REQUEST_TONES } from "../../Components/ui";
import { Backdrop, Modal, Header, Body, Footer, Field, inputStyle, btnPrimary as mBtnPrimary, btnGhost as mBtnGhost } from "../Leaves/ApplyLeaveModal";

// Shared approval-lifecycle tones (see Components/ui/statusTones.js). Was a
// private copy of four hardcoded light-theme pairs, duplicated across five
// pages and unreadable-looking in dark mode.
const STATUS_STYLE = REQUEST_TONES;

export default function WfhPage() {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useShellHeader({ title: "WFH Requests" }, []);

  async function load() {
    setLoading(true);
    try {
      const r = await api("/attendance/wfh/my");
      setRows(Array.isArray(r) ? r : (r.rows || r.wfh || r.records || []));
    } catch (e) { toast({ title: e.message, status: "error" }); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function cancel(r) {
    const msg = r.status === "approved"
      ? `Cancel your approved WFH for ${fmtDate(r.fromDate)}${fmtDate(r.fromDate) === fmtDate(r.toDate) ? "" : " → " + fmtDate(r.toDate)}? Admins will be notified.`
      : "Cancel this WFH request?";
    if (!window.confirm(msg)) return;
    try {
      await api(`/attendance/wfh/${r._id}`, { method: "DELETE" });
      toast({ title: "WFH cancelled.", status: "success" });
      load();
    } catch (e) { toast({ title: e.data?.message || e.message, status: "error" }); }
  }

  // Owner can cancel pending WFH, or APPROVED WFH whose fromDate is strictly in the future.
  function canCancel(r) {
    if (r.status === "pending") return true;
    if (r.status !== "approved") return false;
    const now = new Date();
    const todayStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    return new Date(r.fromDate) > todayStart;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Header row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: "var(--text-primary)", margin: 0 }}>🏠 Work From Home Requests</h2>
          <div style={{ fontSize: 13, color: "var(--text-tertiary)", marginTop: 4 }}>
            Submit in advance. Approved WFH days allow check-in from any network.
          </div>
        </div>
        <button onClick={() => setOpen(true)} style={btnPrimary}>+ Request WFH</button>
      </div>

      {/* How WFH works info card */}
      <div style={{
        background: "linear-gradient(135deg, var(--bg-surface) 0%, var(--tone-violet-bg) 100%)",
        border: "1px solid var(--tone-violet-br)",
        borderRadius: 12, padding: "16px 20px", color: "var(--tone-violet-fg)",
      }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>How WFH works</div>
        <ul style={{ margin: 0, paddingLeft: 22, fontSize: 13, lineHeight: 1.7 }}>
          <li>Submit request with dates and a reason.</li>
          <li>Admin approves or rejects it.</li>
          <li>On an approved WFH day, check-in works from any network and is recorded as <strong>WFH</strong>.</li>
          <li>Without approval, check-in is blocked outside the office WiFi.</li>
        </ul>
      </div>

      <Card padding="none">
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <thead>
              <tr><Th>From</Th><Th>To</Th><Th>Reason</Th><Th>Status</Th><Th>Admin Note</Th><Th>Action</Th></tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><Td colSpan={6} style={{ textAlign: "center", color: "var(--text-tertiary)" }}>Loading…</Td></tr>
              ) : rows.length === 0 ? (
                <tr><Td colSpan={6} style={{ textAlign: "center", color: "var(--text-tertiary)", padding: "24px 12px" }}>No WFH requests yet.</Td></tr>
              ) : rows.map((r) => {
                const s = STATUS_STYLE[r.status] || STATUS_STYLE.pending;
                return (
                  <tr key={r._id}>
                    <Td className="tabular">{fmtDate(r.fromDate)}</Td>
                    <Td className="tabular">{fmtDate(r.toDate)}</Td>
                    <Td style={{ maxWidth: 360, color: "var(--text-secondary)" }}>{r.reason || "—"}</Td>
                    <Td>
                      <span style={{
                        background: s.bg, color: s.co, padding: "2px 10px",
                        borderRadius: 12, fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                      }}>{r.status}</span>
                    </Td>
                    <Td style={{ color: "var(--text-tertiary)", fontSize: 12 }}>{r.adminNote || "—"}</Td>
                    <Td>{canCancel(r) ? (
                      <button onClick={() => cancel(r)} style={btnDanger}>Cancel</button>
                    ) : "—"}</Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <RequestWfhModal open={open} onClose={() => setOpen(false)} onSubmitted={() => { setOpen(false); load(); }} />
    </div>
  );
}

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function RequestWfhModal({ open, onClose, onSubmitted }) {
  const toast = useToast();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const today = todayIso();
  const isAdmin = localStorage.getItem("role") === "SUPER_ADMIN";

  if (!open) return null;

  async function submit() {
    if (!from || !to || !reason.trim()) { toast({ title: "All fields required.", status: "warning" }); return; }
    if (new Date(from) > new Date(to)) { toast({ title: "From date cannot be after To date.", status: "warning" }); return; }
    if (!isAdmin && from < today) {
      toast({ title: "WFH cannot start in the past. Submit before the date.", status: "warning" });
      return;
    }
    setBusy(true);
    try {
      await api("/attendance/wfh", { method: "POST", body: JSON.stringify({ fromDate: from, toDate: to, reason }) });
      toast({ title: "WFH request submitted. Awaiting admin approval.", status: "success" });
      setFrom(""); setTo(""); setReason("");
      onSubmitted && onSubmitted();
    } catch (e) {
      toast({ title: e.data?.message || e.message, status: "error" });
    } finally { setBusy(false); }
  }

  return (
    <Backdrop onClose={onClose}>
      <Modal>
        <Header title="🏠 Request Work From Home" onClose={onClose} />
        <Body>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="FROM DATE *"><input type="date" value={from} min={isAdmin ? undefined : today} onChange={(e) => setFrom(e.target.value)} style={inputStyle} /></Field>
            <Field label="TO DATE *"><input type="date" value={to} min={isAdmin ? undefined : (from || today)} onChange={(e) => setTo(e.target.value)} style={inputStyle} /></Field>
          </div>
          <Field label="REASON *">
            <textarea
              value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Why are you working from home? (e.g. doctor visit, travel, personal work...)"
              style={{ ...inputStyle, minHeight: 90, resize: "vertical" }}
            />
          </Field>
          <div style={{
            background: "var(--tone-violet-bg)", border: "1px solid var(--tone-violet-br)",
            borderRadius: 8, padding: "10px 12px", fontSize: 12, color: "var(--tone-violet-fg)",
          }}>
            ⚡ <strong>WFH must be approved before the date.</strong> On an approved WFH day, you can check in from any network and it will be recorded as WFH.
          </div>
        </Body>
        <Footer>
          <button onClick={onClose} style={mBtnGhost} disabled={busy}>Cancel</button>
          <button onClick={submit} style={mBtnPrimary} disabled={busy}>{busy ? "Submitting…" : "Submit Request"}</button>
        </Footer>
      </Modal>
    </Backdrop>
  );
}

function fmtDate(d) { try { return new Date(d).toISOString().slice(0, 10); } catch { return "—"; } }

const thStyle = {
  textAlign: "left", padding: "10px 14px", fontSize: 11, fontWeight: 600,
  textTransform: "uppercase", letterSpacing: "0.06em",
  color: "var(--text-tertiary)", background: "var(--bg-subtle)",
  borderBottom: "1px solid var(--border-subtle)",
};
const tdBase = { padding: "10px 14px", borderTop: "1px solid var(--border-subtle)", color: "var(--text-primary)" };
const btnPrimary = {
  background: "var(--accent-primary)", color: "white", border: "none",
  padding: "9px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
};
const btnDanger = {
  background: "transparent", color: "var(--tone-danger-fg)",
  padding: "4px 10px", border: "1px solid var(--border-default)", borderRadius: 6,
  fontSize: 12, cursor: "pointer", fontWeight: 500,
};
function Th({ children }) { return <th style={thStyle}>{children}</th>; }
function Td({ children, style, className, colSpan }) { return <td colSpan={colSpan} style={{ ...tdBase, ...style }} className={className}>{children}</td>; }
